import { configureStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { TypedUseSelectorHook, useDispatch, useSelector, useStore } from "react-redux";
import { reducers, serializableCheck } from "./reducers";
import { sentryReduxEnhancerOptions } from "./sentryRedux";

// Allowlisted state, type-only action breadcrumbs, no state attachment — see `store/sentryRedux.ts`.
const sentryReduxEnhancer = Sentry.createReduxEnhancer(sentryReduxEnhancerOptions);

const store = configureStore({
  reducer: reducers,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck }),
  enhancers: [sentryReduxEnhancer],
});

export default store;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
/**
 * The store itself, for the rare case where a *live* read is required rather than the render's
 * snapshot: `useAppSelector` returns the value from the current render, so an effect that runs twice
 * in one commit (StrictMode) sees the same stale value both times and cannot tell that its first
 * pass already acted. `useGuideCoordinator` guards its analytics emission this way.
 *
 * Not a general-purpose escape hatch — reading state to *render* it must still go through
 * `useAppSelector`, or the component won't re-render when it changes.
 */
export const useAppStore = () => useStore<RootState>();
